const STORAGE_KEY = "downloadPerfDebug";
const DEFAULT_SCENARIO = "unlabeled";
const ITEM_STATE_ORDER = Object.freeze({
  declared: 0,
  received: 1,
  parsed: 2,
  renderable: 3
});

const sessions = [];
const activeSeriesSessions = new Map();
let currentScenario = {
  name: DEFAULT_SCENARIO,
  startedAtIso: new Date().toISOString()
};

const now = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const nowIso = () => new Date().toISOString();

const isObject = value =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const readStorage = key => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};

const writeStorage = (key, value) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (_) {
    // ignore storage errors
  }
};

const isEnabled = () => readStorage(STORAGE_KEY) === "1";

const toNumber = value => (Number.isFinite(value) ? value : null);

const normalizeItemState = state => {
  if (typeof state !== "string") {
    return null;
  }
  const normalized = state.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(ITEM_STATE_ORDER, normalized)
    ? normalized
    : null;
};

const setImageState = (image, state) => {
  const nextState = normalizeItemState(state);
  if (!image || !nextState) return;
  const currentState = normalizeItemState(image.itemState) || "declared";
  if (ITEM_STATE_ORDER[nextState] < ITEM_STATE_ORDER[currentState]) {
    return;
  }
  image.itemState = nextState;
};

const summarizeSamples = samples => {
  const values = samples
    .filter(value => Number.isFinite(value) && value >= 0)
    .sort((a, b) => a - b);
  if (!values.length) {
    return {
      count: 0,
      avg: null,
      p50: null,
      p95: null,
      max: null
    };
  }

  const percentile = ratio => {
    const index = Math.min(
      values.length - 1,
      Math.max(0, Math.ceil(values.length * ratio) - 1)
    );
    return Math.round(values[index] * 100) / 100;
  };

  const sum = values.reduce((acc, value) => acc + value, 0);
  return {
    count: values.length,
    avg: Math.round((sum / values.length) * 100) / 100,
    p50: percentile(0.5),
    p95: percentile(0.95),
    max: Math.round(values[values.length - 1] * 100) / 100
  };
};

const durationBetween = (record, startKey, endKey) => {
  if (!record) return null;
  const start = record[startKey];
  const end = record[endKey];
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return null;
  }
  return end - start;
};

const buildStageBreakdown = images => {
  const stages = {
    queue_wait_ms: [],
    time_to_first_chunk_ms: [],
    download_ms: [],
    parse_wait_ms: [],
    parse_ms: [],
    post_parse_stack_update_ms: [],
    request_to_stack_update_ms: []
  };

  Object.values(images).forEach(image => {
    const queueWait = durationBetween(image, "enqueuedAt", "requestSentAt");
    if (queueWait !== null) stages.queue_wait_ms.push(queueWait);

    const requestToFirstChunk = durationBetween(
      image,
      "requestSentAt",
      "firstChunkAt"
    );
    if (requestToFirstChunk !== null) {
      stages.time_to_first_chunk_ms.push(requestToFirstChunk);
    }

    const download = durationBetween(image, "firstChunkAt", "lastChunkAt");
    if (download !== null) stages.download_ms.push(download);

    const parseWait = durationBetween(image, "lastChunkAt", "parseStartAt");
    if (parseWait !== null) stages.parse_wait_ms.push(parseWait);

    const parse = durationBetween(image, "parseStartAt", "parseEndAt");
    if (parse !== null) stages.parse_ms.push(parse);

    const postParse = durationBetween(image, "parseEndAt", "stackUpdateAt");
    if (postParse !== null) stages.post_parse_stack_update_ms.push(postParse);

    const requestToStack = durationBetween(
      image,
      "requestSentAt",
      "stackUpdateAt"
    );
    if (requestToStack !== null) {
      stages.request_to_stack_update_ms.push(requestToStack);
    }
  });

  const summary = Object.fromEntries(
    Object.entries(stages).map(([key, values]) => [key, summarizeSamples(values)])
  );

  const totals = {
    queue_wait_ms: stages.queue_wait_ms.reduce((acc, value) => acc + value, 0),
    network_ms:
      stages.time_to_first_chunk_ms.reduce((acc, value) => acc + value, 0) +
      stages.download_ms.reduce((acc, value) => acc + value, 0),
    parse_wait_ms: stages.parse_wait_ms.reduce((acc, value) => acc + value, 0),
    parse_ms: stages.parse_ms.reduce((acc, value) => acc + value, 0),
    post_parse_stack_update_ms: stages.post_parse_stack_update_ms.reduce(
      (acc, value) => acc + value,
      0
    )
  };

  const totalMeasured = Object.values(totals).reduce((acc, value) => acc + value, 0);
  const dominantStage =
    Object.entries(totals).sort((left, right) => right[1] - left[1])[0] || null;

  return {
    timings: summary,
    bottleneck: {
      dominant_stage: dominantStage ? dominantStage[0] : null,
      totals_ms: Object.fromEntries(
        Object.entries(totals).map(([key, value]) => [
          key,
          Math.round(value * 100) / 100
        ])
      ),
      percentages:
        totalMeasured > 0
          ? Object.fromEntries(
              Object.entries(totals).map(([key, value]) => [
                key,
                Math.round((10000 * value) / totalMeasured) / 100
              ])
            )
          : {}
    }
  };
};

const ensureSession = seriesId => {
  const key = String(seriesId);
  return activeSeriesSessions.get(key) || null;
};

const ensureImage = (session, dbImageId, extra = {}) => {
  if (!session) return null;
  const key = String(dbImageId);
  if (!session.images[key]) {
    session.images[key] = {
      dbImageId,
      createdAt: now(),
      itemState: "declared"
    };
  }
  Object.keys(extra).forEach(extraKey => {
    const value = extra[extraKey];
    if (value === undefined) return;
    if (session.images[key][extraKey] === undefined) {
      session.images[key][extraKey] = value;
    }
  });
  return session.images[key];
};

const updatePipelineStats = (session, metrics = {}) => {
  if (!session) return;
  const { inFlight, parseQueueLength, pendingChunkCount } = metrics;
  if (Number.isFinite(inFlight)) {
    session.pipeline.maxInFlight = Math.max(session.pipeline.maxInFlight, inFlight);
  }
  if (Number.isFinite(parseQueueLength)) {
    session.pipeline.maxParseQueue = Math.max(
      session.pipeline.maxParseQueue,
      parseQueueLength
    );
  }
  if (Number.isFinite(pendingChunkCount)) {
    session.pipeline.maxPendingChunks = Math.max(
      session.pipeline.maxPendingChunks,
      pendingChunkCount
    );
  }
};

const recordError = ({ seriesId, stage, dbImageId = null, message }) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  if (!session) return;
  session.errors.push({
    atIso: nowIso(),
    stage,
    dbImageId,
    message: String(message || "")
  });
};

const startSeries = ({
  seriesId,
  elementId,
  totalImages = 0,
  imageIds = [],
  label = null,
  metadata = {}
}) => {
  if (!isEnabled()) return null;

  const startedAt = now();
  const session = {
    sessionId: `${seriesId}-${Date.now()}`,
    seriesId,
    elementId,
    label,
    totalImages,
    metadata: isObject(metadata) ? metadata : {},
    scenario: { ...currentScenario },
    startedAt,
    startedAtIso: nowIso(),
    completedAt: null,
    completedAtIso: null,
    images: {},
    pipeline: {
      maxInFlight: 0,
      maxParseQueue: 0,
      maxPendingChunks: 0
    },
    navigation: {
      futureSliceAttempts: [],
      queueReprioritizations: []
    },
    completionChecks: [],
    errors: []
  };

  imageIds.forEach((dbImageId, index) => {
    session.images[String(dbImageId)] = {
      dbImageId,
      orderIndex: index,
      enqueuedAt: startedAt,
      itemState: "declared"
    };
  });

  sessions.push(session);
  activeSeriesSessions.set(String(seriesId), session);
  return session;
};

const markRequestSent = ({
  seriesId,
  dbImageId,
  elementId,
  inFlight,
  parseQueueLength,
  pendingChunkCount
}) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId, { elementId });
  if (!image) return;
  if (!Number.isFinite(image.enqueuedAt)) {
    image.enqueuedAt = session.startedAt;
  }
  image.requestSentAt = now();
  updatePipelineStats(session, { inFlight, parseQueueLength, pendingChunkCount });
};

const markFirstChunk = ({ seriesId, dbImageId, totalSize = null }) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId);
  if (!image) return;
  if (!Number.isFinite(image.firstChunkAt)) {
    image.firstChunkAt = now();
  }
  if (Number.isFinite(totalSize) && totalSize > 0) {
    image.totalSize = totalSize;
  }
};

const markChunk = ({
  seriesId,
  dbImageId,
  bytes = 0,
  chunkIndex,
  inFlight,
  parseQueueLength,
  pendingChunkCount
}) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId);
  if (!image) return;
  if (!Number.isFinite(image.firstChunkAt)) {
    image.firstChunkAt = now();
  }
  image.chunkCount = (image.chunkCount || 0) + 1;
  image.bytesReceived = (image.bytesReceived || 0) + (bytes || 0);
  image.lastObservedChunkIndex = chunkIndex;
  updatePipelineStats(session, { inFlight, parseQueueLength, pendingChunkCount });
};

const markLastChunk = ({ seriesId, dbImageId, parseQueueLength }) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId);
  if (!image) return;
  image.lastChunkAt = now();
  image.parseQueuedAt = now();
  setImageState(image, "received");
  updatePipelineStats(session, { parseQueueLength });
};

const markParseStart = ({ seriesId, dbImageId, parserUsed, parseQueueLength }) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId);
  if (!image) return;
  image.parseStartAt = now();
  image.parserUsed = parserUsed;
  updatePipelineStats(session, { parseQueueLength });
};

const markParseEnd = ({ seriesId, dbImageId }) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId);
  if (!image) return;
  image.parseEndAt = now();
  setImageState(image, "parsed");
};

const markStackUpdate = ({
  seriesId,
  dbImageId,
  imageIdsLength = null,
  currentImageId = null
}) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId);
  if (!image) return;
  image.stackUpdateAt = now();
  setImageState(image, "renderable");
  image.imageIdsLength = toNumber(imageIdsLength);
  if (currentImageId !== null && currentImageId !== undefined) {
    image.currentImageId = currentImageId;
  }
};

const markItemState = ({ seriesId, dbImageId, state }) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  const image = ensureImage(session, dbImageId);
  if (!image) return;
  setImageState(image, state);
};

const markCompletionCheck = ({
  seriesId,
  reason = null,
  canFinalize = false,
  blocker = null,
  declaredCount = null,
  renderableCount = null,
  inFlightItemIds = [],
  parseQueuedItemIds = [],
  pendingChunkCount = null,
  parseQueueLength = null,
  lastDeclaredItemId = null,
  lastDeclaredItemState = null,
  unresolvedItemIds = []
}) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  if (!session) return;
  session.completionChecks.push({
    atIso: nowIso(),
    reason,
    canFinalize: !!canFinalize,
    blocker,
    declaredCount: toNumber(declaredCount),
    renderableCount: toNumber(renderableCount),
    inFlightItemIds: Array.isArray(inFlightItemIds) ? inFlightItemIds : [],
    parseQueuedItemIds: Array.isArray(parseQueuedItemIds) ? parseQueuedItemIds : [],
    pendingChunkCount: toNumber(pendingChunkCount),
    parseQueueLength: toNumber(parseQueueLength),
    lastDeclaredItemId,
    lastDeclaredItemState: normalizeItemState(lastDeclaredItemState),
    unresolvedItemIds: Array.isArray(unresolvedItemIds) ? unresolvedItemIds : []
  });
};

const markSeriesComplete = ({ seriesId }) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  if (!session) return;
  session.completedAt = now();
  session.completedAtIso = nowIso();
  activeSeriesSessions.delete(String(seriesId));
};

const markFutureSliceAttempt = ({
  seriesId,
  elementId,
  requestedSliceId,
  loadedImageCount,
  declaredMaxSliceId
}) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  if (!session) return;
  session.navigation.futureSliceAttempts.push({
    atIso: nowIso(),
    elementId,
    requestedSliceId,
    loadedImageCount,
    declaredMaxSliceId
  });
};

const markQueueReprioritize = ({
  seriesId,
  requestedSliceId,
  reason = null,
  pendingCount = null,
  loadedImageCount = null,
  declaredMaxSliceId = null,
  elementId = null
}) => {
  if (!isEnabled()) return;
  const session = ensureSession(seriesId);
  if (!session) return;
  session.navigation.queueReprioritizations.push({
    atIso: nowIso(),
    elementId,
    requestedSliceId: toNumber(requestedSliceId),
    reason,
    pendingCount: toNumber(pendingCount),
    loadedImageCount: toNumber(loadedImageCount),
    declaredMaxSliceId: toNumber(declaredMaxSliceId)
  });
};

const summarizeSession = session => {
  if (!session) return null;

  const images = session.images || {};
  const imageList = Object.values(images);
  const completedImages = imageList.filter(image =>
    Number.isFinite(image.stackUpdateAt)
  );
  const stageBreakdown = buildStageBreakdown(images);
  const itemStateCounts = {
    declared: 0,
    received: 0,
    parsed: 0,
    renderable: 0
  };
  imageList.forEach(image => {
    const state = normalizeItemState(image.itemState) || "declared";
    itemStateCounts[state] += 1;
  });
  const completionChecks = Array.isArray(session.completionChecks)
    ? session.completionChecks
    : [];

  return {
    session_id: session.sessionId,
    scenario: session.scenario?.name || DEFAULT_SCENARIO,
    scenario_started_at: session.scenario?.startedAtIso || null,
    series: {
      id: session.seriesId,
      label: session.label,
      element_id: session.elementId,
      total_images_declared: session.totalImages,
      images_seen: imageList.length,
      images_completed: completedImages.length
    },
    metadata: session.metadata,
    lifecycle: {
      started_at: session.startedAtIso,
      completed_at: session.completedAtIso,
      total_wall_ms:
        Number.isFinite(session.startedAt) && Number.isFinite(session.completedAt)
          ? Math.round((session.completedAt - session.startedAt) * 100) / 100
          : null
    },
    pipeline: {
      max_in_flight: session.pipeline.maxInFlight,
      max_parse_queue: session.pipeline.maxParseQueue,
      max_pending_chunks: session.pipeline.maxPendingChunks
    },
    counts: {
      first_chunk_seen: imageList.filter(image => Number.isFinite(image.firstChunkAt))
        .length,
      last_chunk_seen: imageList.filter(image => Number.isFinite(image.lastChunkAt))
        .length,
      parse_started: imageList.filter(image => Number.isFinite(image.parseStartAt))
        .length,
      parse_completed: imageList.filter(image => Number.isFinite(image.parseEndAt))
        .length,
      item_states: itemStateCounts,
      completion_checks: completionChecks.length,
      future_slice_attempts: session.navigation.futureSliceAttempts.length,
      queue_reprioritizations: session.navigation.queueReprioritizations.length,
      total_chunks: imageList.reduce(
        (acc, image) => acc + (image.chunkCount || 0),
        0
      ),
      total_bytes_received: imageList.reduce(
        (acc, image) => acc + (image.bytesReceived || 0),
        0
      )
    },
    timings_ms: stageBreakdown.timings,
    bottleneck: stageBreakdown.bottleneck,
    completion: {
      checks: completionChecks,
      last_check: completionChecks.length
        ? completionChecks[completionChecks.length - 1]
        : null
    },
    future_slice_attempts: session.navigation.futureSliceAttempts,
    queue_reprioritizations: session.navigation.queueReprioritizations,
    errors: session.errors
  };
};

const dumpLatest = () => {
  const latest = sessions.length ? sessions[sessions.length - 1] : null;
  return JSON.stringify(summarizeSession(latest), null, 2);
};

const dumpAll = () =>
  JSON.stringify(sessions.map(session => summarizeSession(session)), null, 2);

const latestSummary = () => {
  const latest = sessions.length ? sessions[sessions.length - 1] : null;
  return summarizeSession(latest);
};

const setEnabled = value => {
  writeStorage(STORAGE_KEY, value ? "1" : null);
  return isEnabled();
};

const clear = () => {
  sessions.length = 0;
  activeSeriesSessions.clear();
  currentScenario = {
    name: DEFAULT_SCENARIO,
    startedAtIso: nowIso()
  };
};

const startScenario = (name, metadata = {}) => {
  currentScenario = {
    name: String(name || DEFAULT_SCENARIO),
    startedAtIso: nowIso(),
    metadata: isObject(metadata) ? metadata : {}
  };
  return currentScenario;
};

const api = {
  isEnabled,
  setEnabled,
  clear,
  startScenario,
  latestSummary,
  dumpLatest,
  dumpAll,
  startSeries,
  markRequestSent,
  markFirstChunk,
  markChunk,
  markLastChunk,
  markParseStart,
  markParseEnd,
  markItemState,
  markStackUpdate,
  markCompletionCheck,
  markSeriesComplete,
  markFutureSliceAttempt,
  markQueueReprioritize,
  recordError
};

if (typeof window !== "undefined") {
  window.__dvDownloadPerf = api;
}

export default api;
